import { forwardRef, useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../Button';
import { formatDateTimeShort } from '../../../lib/format-date';
import { fromWallClock, todayInTimezone, toWallClock } from '../../../lib/timezone';

import type { DateRange, DatePickerProps, DatePickerShortcut, CalendarView } from './types';
import {
  formatDateFR,
  formatDateTimeFR,
  getDefaultShortcuts,
  isDateDisabled,
  isMonthFullyDisabled,
  isShortcutDisabled,
  applyTimeToDate,
  parseDateParam,
} from './utils';
import { CalendarGrid } from './CalendarGrid';
import { MonthPicker } from './MonthPicker';
import { YearPicker } from './YearPicker';
import { TimeInputs } from './TimeInputs';
import { Shortcuts } from './Shortcuts';

export type { DateRange, DatePickerProps, DatePickerShortcut, DatePickerConfigs } from './types';
export {
  parseDateParam,
  formatDateParam,
  formatDateParamDisplay,
  isDateParamToday,
  dateParamHasTime,
  formatDateFR,
  formatTimeFR,
  formatTimeSecondsFR,
  formatDateTimeFR,
  formatRelativeTimeFR,
  type RelativeTimeFormat,
} from './utils';

const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder,
      className,
      asSingle = false,
      showTime = false,
      minDate,
      maxDate,
      configs,
      label,
      disabled = false,
      timezone,
    },
    ref
  ) => {
    // --- Fuseau du site (optionnel) ---
    // Mode "instants" : avec un fuseau ET la saisie de l'heure, `value` porte
    // de vrais instants. En interne le calendrier ne manipule que des jours
    // civils (Date a minuit local, sans heure) et l'heure vit a part
    // (startHours...) : aucune conversion ne passe par le fuseau de la machine.
    const isZoned = !!timezone && showTime;

    /** Jour civil "aujourd'hui" : celui du site si un fuseau est fourni. */
    const getToday = useCallback((): Date => {
      const siteToday = timezone ? parseDateParam(todayInTimezone(timezone)) : null;
      if (siteToday) return siteToday;
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }, [timezone]);

    /** Decompose une valeur en jour civil (champs locaux) + heure affichee. */
    const splitValue = useCallback(
      (date: Date | null): { day: Date; hours: number; minutes: number } | null => {
        if (!date) return null;
        if (isZoned) {
          const wall = toWallClock(date, timezone);
          if (!wall) return null;
          return {
            day: new Date(wall.year, wall.month - 1, wall.day),
            hours: wall.hour,
            minutes: wall.minute,
          };
        }
        return { day: date, hours: date.getHours(), minutes: date.getMinutes() };
      },
      [isZoned, timezone]
    );

    // --- State ---
    const [isOpen, setIsOpen] = useState(false);
    const [leftMonth, setLeftMonth] = useState(() => {
      const date = splitValue(value.start)?.day ?? getToday();
      return { year: date.getFullYear(), month: date.getMonth() };
    });

    // Draft state: internal selection, only committed on "Appliquer"
    const [draft, setDraft] = useState<DateRange>({ start: null, end: null });
    const [selectionStart, setSelectionStart] = useState<Date | null>(null);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);

    const [calendarView, setCalendarView] = useState<CalendarView>('days');
    const [viewCalendarSide, setViewCalendarSide] = useState<'left' | 'right'>('left');
    const [yearsBlockStart, setYearsBlockStart] = useState(() => {
      const baseYear = (splitValue(value.start)?.day ?? getToday()).getFullYear();
      return baseYear - (baseYear % 12);
    });

    // Draft time state
    const [startHours, setStartHours] = useState(() => splitValue(value.start)?.hours ?? 0);
    const [startMinutes, setStartMinutes] = useState(() => splitValue(value.start)?.minutes ?? 0);
    const [endHours, setEndHours] = useState(() => splitValue(value.end)?.hours ?? 23);
    const [endMinutes, setEndMinutes] = useState(() => splitValue(value.end)?.minutes ?? 59);

    // Mobile detection
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

    // Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({ top: 0, left: 0 });

    // --- Derived ---
    const effectiveSingle = asSingle || isMobile;

    const rightMonth = useMemo(() => {
      const next = leftMonth.month + 1;
      return {
        year: next > 11 ? leftMonth.year + 1 : leftMonth.year,
        month: next > 11 ? 0 : next,
      };
    }, [leftMonth]);

    const shortcuts = useMemo(() => {
      if (asSingle && !configs?.shortcuts) return null;
      // `isOpen` en dependance : "aujourd'hui" est recalcule a chaque ouverture.
      return configs?.shortcuts ?? getDefaultShortcuts(getToday());
    }, [asSingle, configs?.shortcuts, getToday, isOpen]);

    const defaultPlaceholder = asSingle ? 'Sélectionner une date' : 'Sélectionner une période';

    // Trigger displays committed value (not draft)
    const displayValue = useMemo(() => {
      if (isZoned) {
        // Instants : affiches a l'heure du site (meme format que formatDateTimeFR).
        const startStr = value.start ? formatDateTimeShort(value.start, timezone) : '';
        if (asSingle || !value.end) return startStr;
        return startStr ? `${startStr} - ${formatDateTimeShort(value.end, timezone)}` : '';
      }
      if (asSingle) {
        if (!value.start) return '';
        return showTime ? formatDateTimeFR(value.start) : formatDateFR(value.start);
      }
      if (value.start && value.end) {
        const startStr = showTime ? formatDateTimeFR(value.start) : formatDateFR(value.start);
        const endStr = showTime ? formatDateTimeFR(value.end) : formatDateFR(value.end);
        return `${startStr} - ${endStr}`;
      }
      if (value.start) {
        return showTime ? formatDateTimeFR(value.start) : formatDateFR(value.start);
      }
      return '';
    }, [value, asSingle, showTime, isZoned, timezone]);

    // Calendar highlights use draft (with hover for range)
    const highlightRange = useMemo(() => {
      if (selectionStart && hoverDate) {
        return hoverDate < selectionStart
          ? { start: hoverDate, end: selectionStart }
          : { start: selectionStart, end: hoverDate };
      }
      return { start: draft.start, end: draft.end };
    }, [selectionStart, hoverDate, draft.start, draft.end]);

    // Is the draft complete enough to apply?
    const canApply = asSingle ? !!draft.start : !!(draft.start && draft.end);

    // Nav constraints
    const isPrevMonthDisabled = minDate
      ? isMonthFullyDisabled(
          leftMonth.month === 0 ? leftMonth.year - 1 : leftMonth.year,
          leftMonth.month === 0 ? 11 : leftMonth.month - 1,
          minDate,
          undefined
        )
      : false;

    const isNextMonthDisabled = maxDate
      ? effectiveSingle
        ? isMonthFullyDisabled(
            leftMonth.month === 11 ? leftMonth.year + 1 : leftMonth.year,
            leftMonth.month === 11 ? 0 : leftMonth.month + 1,
            undefined,
            maxDate
          )
        : isMonthFullyDisabled(
            rightMonth.month === 11 ? rightMonth.year + 1 : rightMonth.year,
            rightMonth.month === 11 ? 0 : rightMonth.month + 1,
            undefined,
            maxDate
          )
      : false;

    // --- Effects ---
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < 640);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Initialize draft when opening
    const handleOpen = () => {
      if (disabled) return;
      const start = splitValue(value.start);
      const end = splitValue(value.end);
      setDraft({ start: start?.day ?? null, end: end?.day ?? null });
      setSelectionStart(null);
      setHoverDate(null);
      setCalendarView('days');
      // Sync time from committed value
      setStartHours(start?.hours ?? 0);
      setStartMinutes(start?.minutes ?? 0);
      setEndHours(end?.hours ?? 23);
      setEndMinutes(end?.minutes ?? 59);
      // Navigate calendar to value's month
      const date = start?.day ?? getToday();
      setLeftMonth({ year: date.getFullYear(), month: date.getMonth() });
      setIsOpen(true);
    };

    // Smart dropdown positioning
    const updateDropdownPosition = useCallback(() => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();

      if (isMobile) {
        setDropdownStyle({ position: 'fixed', top: rect.bottom + 8, left: 8, right: 8, zIndex: 9999 });
        return;
      }

      const estimatedHeight = showTime ? 440 : 380;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const showAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

      setDropdownStyle({
        position: 'fixed',
        top: showAbove ? rect.top - estimatedHeight - 4 : rect.bottom + 8,
        left: rect.left,
        zIndex: 9999,
      });
    }, [isMobile, showTime]);

    useLayoutEffect(() => {
      if (isOpen) updateDropdownPosition();
    }, [isOpen, updateDropdownPosition]);

    // Reposition on scroll/resize
    useEffect(() => {
      if (!isOpen) return;
      const handle = () => updateDropdownPosition();
      window.addEventListener('scroll', handle, true);
      window.addEventListener('resize', handle);
      return () => {
        window.removeEventListener('scroll', handle, true);
        window.removeEventListener('resize', handle);
      };
    }, [isOpen, updateDropdownPosition]);

    // Close on outside click (= cancel, no commit)
    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          containerRef.current &&
          !containerRef.current.contains(target) &&
          triggerRef.current &&
          !triggerRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Update years block when switching to years view
    useEffect(() => {
      if (calendarView === 'years') {
        const baseYear = viewCalendarSide === 'left' ? leftMonth.year : rightMonth.year;
        setYearsBlockStart(baseYear - (baseYear % 12));
      }
    }, [calendarView, viewCalendarSide, leftMonth.year, rightMonth.year]);

    // --- Handlers ---
    const handlePrevMonth = () => {
      setLeftMonth((prev) => {
        const m = prev.month - 1;
        return { year: m < 0 ? prev.year - 1 : prev.year, month: m < 0 ? 11 : m };
      });
    };

    const handleNextMonth = () => {
      setLeftMonth((prev) => {
        const m = prev.month + 1;
        return { year: m > 11 ? prev.year + 1 : prev.year, month: m > 11 ? 0 : m };
      });
    };

    // Day click updates draft only (never calls onChange)
    const handleDayClick = (date: Date) => {
      if (isDateDisabled(date, minDate, maxDate)) return;

      if (asSingle) {
        setDraft({ start: date, end: date });
        setSelectionStart(null);
        return;
      }

      // Range mode: two-click selection
      if (!selectionStart) {
        setSelectionStart(date);
        setDraft({ start: date, end: null });
      } else {
        let start: Date, end: Date;
        if (date < selectionStart) {
          start = date;
          end = selectionStart;
        } else {
          start = selectionStart;
          end = date;
        }
        setDraft({ start, end });
        setSelectionStart(null);
      }
    };

    // Shortcut updates draft only (never calls onChange)
    const handleQuickRange = (shortcut: DatePickerShortcut) => {
      if (isShortcutDisabled(shortcut, minDate, maxDate)) return;
      setDraft({ start: shortcut.start, end: shortcut.end });
      setSelectionStart(null);
    };

    // Clear committed value directly (trigger X button)
    const handleClear = () => {
      onChange({ start: null, end: null });
      setSelectionStart(null);
    };

    // Appliquer: commit draft → onChange
    const handleApply = () => {
      if (!draft.start) return;
      let start = draft.start;
      let end = draft.end;

      if (isZoned) {
        // Jour civil choisi + heure saisie = heure murale du site -> instant.
        const toInstant = (day: Date, hours: number, minutes: number): Date =>
          fromWallClock(
            {
              year: day.getFullYear(),
              month: day.getMonth() + 1,
              day: day.getDate(),
              hour: hours,
              minute: minutes,
              second: 0,
              millisecond: 0,
            },
            timezone
          );
        const endDay = end ?? start;
        start = toInstant(start, startHours, startMinutes);
        end = toInstant(endDay, endHours, endMinutes);
      } else if (showTime) {
        start = applyTimeToDate(start, startHours, startMinutes)!;
        end = end ? applyTimeToDate(end, endHours, endMinutes)! : applyTimeToDate(start, endHours, endMinutes)!;
      }

      if (asSingle) {
        onChange({ start, end: start });
      } else {
        onChange({ start, end });
      }

      setIsOpen(false);
    };

    // Quitter: close without committing (value stays unchanged)
    const handleCancel = () => {
      setIsOpen(false);
      setSelectionStart(null);
    };

    const handleViewChange = (view: CalendarView, side: 'left' | 'right') => {
      setViewCalendarSide(side);
      setCalendarView(view);
    };

    const handleMonthSelect = (month: number) => {
      if (viewCalendarSide === 'left') {
        setLeftMonth((prev) => ({ ...prev, month }));
      } else {
        const m = month - 1;
        setLeftMonth({ year: m < 0 ? leftMonth.year - 1 : leftMonth.year, month: m < 0 ? 11 : m });
      }
      setCalendarView('days');
    };

    const handleYearSelect = (year: number) => {
      setLeftMonth((prev) => ({ ...prev, year }));
      setCalendarView('months');
    };

    const handleMonthPickerYearChange = (delta: number) => {
      setLeftMonth((prev) => ({ ...prev, year: prev.year + delta }));
    };

    const handleYearsBlockChange = (delta: number) => {
      setYearsBlockStart((prev) => prev + delta);
    };

    // --- Calendar area rendering ---
    const renderCalendarArea = () => {
      const today = getToday();
      if (calendarView === 'months') {
        const targetYear = viewCalendarSide === 'left' ? leftMonth.year : rightMonth.year;
        const selectedMonth = viewCalendarSide === 'left' ? leftMonth.month : rightMonth.month;
        return (
          <MonthPicker
            year={targetYear}
            selectedMonth={selectedMonth}
            minDate={minDate}
            maxDate={maxDate}
            onMonthSelect={handleMonthSelect}
            onYearChange={handleMonthPickerYearChange}
            onSwitchToYears={() => setCalendarView('years')}
          />
        );
      }

      if (calendarView === 'years') {
        const selectedYear = viewCalendarSide === 'left' ? leftMonth.year : rightMonth.year;
        return (
          <YearPicker
            yearsBlockStart={yearsBlockStart}
            selectedYear={selectedYear}
            minDate={minDate}
            maxDate={maxDate}
            onYearSelect={handleYearSelect}
            onBlockChange={handleYearsBlockChange}
          />
        );
      }

      // Days view
      if (effectiveSingle) {
        return (
          <CalendarGrid
            year={leftMonth.year}
            month={leftMonth.month}
            showNavLeft
            showNavRight
            isPrevDisabled={isPrevMonthDisabled}
            isNextDisabled={isNextMonthDisabled}
            highlightStart={highlightRange.start}
            highlightEnd={highlightRange.end}
            today={today}
            minDate={minDate}
            maxDate={maxDate}
            asSingle={asSingle}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onDayClick={handleDayClick}
            onDayHover={setHoverDate}
            onViewChange={handleViewChange}
            side="left"
          />
        );
      }

      return (
        <div className="flex gap-6">
          <CalendarGrid
            year={leftMonth.year}
            month={leftMonth.month}
            showNavLeft
            showNavRight={false}
            isPrevDisabled={isPrevMonthDisabled}
            isNextDisabled={false}
            highlightStart={highlightRange.start}
            highlightEnd={highlightRange.end}
            today={today}
            minDate={minDate}
            maxDate={maxDate}
            asSingle={asSingle}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onDayClick={handleDayClick}
            onDayHover={setHoverDate}
            onViewChange={handleViewChange}
            side="left"
          />
          <CalendarGrid
            year={rightMonth.year}
            month={rightMonth.month}
            showNavLeft={false}
            showNavRight
            isPrevDisabled={false}
            isNextDisabled={isNextMonthDisabled}
            highlightStart={highlightRange.start}
            highlightEnd={highlightRange.end}
            today={today}
            minDate={minDate}
            maxDate={maxDate}
            asSingle={asSingle}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onDayClick={handleDayClick}
            onDayHover={setHoverDate}
            onViewChange={handleViewChange}
            side="right"
          />
        </div>
      );
    };

    // --- Render ---
    return (
      <div ref={ref} className={cn('relative', className)}>
        {label && (
          <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
        )}

        {/* Trigger Button */}
        <button
          ref={triggerRef}
          onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center gap-3 rounded-lg border bg-white px-3 text-sm transition-colors',
            'hover:border-neutral-400',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500',
            'border-neutral-300',
            isOpen && 'border-primary-500 ring-2 ring-primary-500/20',
            disabled && 'bg-neutral-50 text-neutral-400 cursor-not-allowed hover:border-neutral-300'
          )}
        >
          <Calendar className="h-4 w-4 text-neutral-400" />
          <span className={cn('flex-1 text-left truncate', displayValue ? 'text-dark-500' : 'text-neutral-400')}>
            {displayValue || placeholder || defaultPlaceholder}
          </span>
          {displayValue && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  handleClear();
                }
              }}
              className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 cursor-pointer"
            >
              <X className="h-4 w-4" />
            </span>
          )}
        </button>

        {/* Dropdown - rendered via portal */}
        {isOpen &&
          createPortal(
            <div
              ref={containerRef}
              style={dropdownStyle}
              className={cn(
                'rounded-xl border border-neutral-200 bg-white shadow-xl',
                'animate-in fade-in-0 zoom-in-95'
              )}
            >
              {/* Mobile shortcuts band */}
              {isMobile && shortcuts && (
                <Shortcuts
                  shortcuts={shortcuts}
                  isMobile
                  minDate={minDate}
                  maxDate={maxDate}
                  onSelect={handleQuickRange}
                />
              )}

              <div className={cn('flex', isMobile && 'flex-col')}>
                {/* Desktop shortcuts sidebar */}
                {!isMobile && shortcuts && (
                  <Shortcuts
                    shortcuts={shortcuts}
                    isMobile={false}
                    minDate={minDate}
                    maxDate={maxDate}
                    onSelect={handleQuickRange}
                  />
                )}

                {/* Calendars + time + footer */}
                <div className="p-4">
                  {renderCalendarArea()}

                  {showTime && calendarView === 'days' && (
                    <TimeInputs
                      asSingle={asSingle}
                      startDate={draft.start}
                      endDate={draft.end}
                      startHours={startHours}
                      startMinutes={startMinutes}
                      endHours={endHours}
                      endMinutes={endMinutes}
                      onStartHoursChange={setStartHours}
                      onStartMinutesChange={setStartMinutes}
                      onEndHoursChange={setEndHours}
                      onEndMinutesChange={setEndMinutes}
                    />
                  )}

                  {/* Footer always visible */}
                  {calendarView === 'days' && (
                    <div className="mt-4 flex justify-end gap-3 border-t border-neutral-200 pt-4">
                      <Button variant="secondary" size="sm" onClick={handleCancel}>
                        Quitter
                      </Button>
                      <Button size="sm" onClick={handleApply} disabled={!canApply}>
                        Appliquer
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export { DatePicker };
